import { useCallback, useEffect, useState } from "react";

import { SessionCourse } from "../domain/sessionExtras";
import { SessionsAPI } from "../infrastructure/api/sessions.api";

export type SessionCourseState = {
    /** Null until loaded, and when it could not be. */
    course: SessionCourse | null;
    loading: boolean;
    saving: boolean;
    saveFailed: boolean;
    assign: (moduleId: string | null) => Promise<void>;
};

/** The course a mentoring belongs to, and the module its mentor assigns. */
export function useSessionCourse(sessionId: string): SessionCourseState {
    const [course, setCourse] = useState<SessionCourse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!sessionId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        SessionsAPI.getCourse(sessionId)
            .then((found) => {
                if (!cancelled) setCourse(found);
            })
            .catch(() => {
                // Nothing to show rather than an error: a session without a
                // course is the ordinary case outside a training centre.
                if (!cancelled) setCourse(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    const assign = useCallback(
        async (moduleId: string | null) => {
            setSaving(true);
            setSaveFailed(false);
            try {
                setCourse(await SessionsAPI.assignCourseModule(sessionId, moduleId));
            } catch {
                setSaveFailed(true);
            } finally {
                setSaving(false);
            }
        },
        [sessionId]
    );

    return { course, loading, saving, saveFailed, assign };
}
